import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Button, Grid, makeStyles, Typography } from '@material-ui/core'
import Icon from '@mdi/react'
import { mdiBarcodeScan, mdiFilePdfBox, mdiOpenInNew } from '@mdi/js'
import copy from 'copy-to-clipboard'

import BaseFilterModal from '../../../components/hooks/BaseFilterModal'
import { SNACKBAR } from '../../main/MainActions'
import colors from '../../../assets/colors'
import { dateFormatedPT } from '../../../util/utils'

import StatusChip, { getInvoiceStatusTone } from './Forms/StatusChip'

const PAYMENT_STATUS_LABEL = {
  paid: 'Pago',
  pending: 'Aguardando pagamento',
  processing: 'Processando',
  refused: 'Recusado',
  failed: 'Falhou',
  canceled: 'Cancelado',
}

const useStyles = makeStyles({
  title: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '8px',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: '20px',
  },
  subtitle: {
    fontSize: '13px',
    color: colors.textPrimary,
    marginBottom: '8px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: `1px solid ${colors.lightGray}`,
  },
  label: {
    fontSize: '13px',
    color: colors.textPrimary,
  },
  value: {
    fontSize: '13px',
    fontWeight: 700,
    color: colors.textCustom,
    textAlign: 'right',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
  action: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    borderRadius: '16px',
    border: `1px solid ${colors.graylight}`,
    color: colors.primary,
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'none',
    padding: '8px 12px',
  },
  warning: {
    fontSize: '13px',
    color: colors.error,
    marginTop: '16px',
  },
})

const AddonChargeDetails = ({ open, handleClose }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const charge = useSelector(state => state.subscription.showAddonCharge)
  const gatewayMessage = useSelector(
    state => state.subscription.showAddonChargeMessage
  )

  const payment = charge?.payment
  const boleto = payment?.boleto
  const card = payment?.card

  const displayStatusLabel = payment
    ? PAYMENT_STATUS_LABEL[payment.status] || payment.status
    : charge?.status_text

  const handleOpenUrl = url => {
    window.open(url, '_blank')
  }

  const handleCopy = async value => {
    try {
      await copy(value)

      dispatch({
        type: SNACKBAR.SOFTSUCCESS,
        success: { message: 'Código copiado com sucesso!' },
      })
    } catch (error) {
      return dispatch({
        type: SNACKBAR.HARDFAIL,
        error: { message: 'Erro ao copiar o codigo!' },
      })
    }
  }

  return (
    <BaseFilterModal
      open={open}
      hiddenHelpButton
      handleOpen={handleClose}
      title="Detalhes da Cobrança"
    >
      <Grid item xs={12} style={{ width: '350px' }}>
        <Box className={classes.title}>
          <Typography
            variant="h4"
            style={{ fontSize: '24px', fontWeight: 'bold' }}
            color={colors.primary}
          >
            {charge?.amount_formatted}
          </Typography>
          <StatusChip
            label={displayStatusLabel}
            tone={getInvoiceStatusTone(null, displayStatusLabel)}
          />
        </Box>

        <Typography className={classes.subtitle}>
          {charge?.amount_formatted} = {charge?.unit_price_formatted} ×{' '}
          {charge?.quantity} un. × {charge?.days_charged}/
          {charge?.commercial_days} dias
        </Typography>

        <Box>
          <Box className={classes.row}>
            <Typography className={classes.label}>Serviço</Typography>
            <Typography className={classes.value}>
              {charge?.addon_service_name}
            </Typography>
          </Box>

          <Box className={classes.row}>
            <Typography className={classes.label}>Origem</Typography>
            <Typography className={classes.value}>
              {charge?.source_text}
            </Typography>
          </Box>

          <Box className={classes.row}>
            <Typography className={classes.label}>Ciclo</Typography>
            <Typography className={classes.value}>
              {dateFormatedPT(charge?.cycle_start_at)} a{' '}
              {dateFormatedPT(charge?.cycle_end_at)}
            </Typography>
          </Box>

          <Box className={classes.row}>
            <Typography className={classes.label}>Método</Typography>
            <Typography className={classes.value}>
              {charge?.payment_method}
            </Typography>
          </Box>

          {boleto?.due_at && (
            <Box className={classes.row}>
              <Typography className={classes.label}>Vencimento</Typography>
              <Typography className={classes.value}>
                {dateFormatedPT(boleto.due_at)}
              </Typography>
            </Box>
          )}

          {card && (
            <Box className={classes.row} style={{ borderBottom: 'none' }}>
              <Typography className={classes.label}>Cartão</Typography>
              <Typography className={classes.value}>
                {card?.brand} final {card?.last_digits}
                {card?.installments > 1 && ` em ${card.installments}x`}
              </Typography>
            </Box>
          )}

          {payment?.failure_reason && (
            <Typography className={classes.warning}>
              {payment.failure_reason}
            </Typography>
          )}

          {!payment && charge?.failure_reason && (
            <Typography className={classes.warning}>
              {charge.failure_reason}
            </Typography>
          )}

          {!payment && !charge?.failure_reason && (
            <Typography className={classes.warning}>
              {gatewayMessage ||
                'Não foi possível carregar o boleto agora. Tente novamente em instantes.'}
            </Typography>
          )}
        </Box>

        {boleto && (
          <Grid item xs={12}>
            <Box className={classes.actions}>
              <Button
                onClick={() => handleOpenUrl(boleto.url)}
                className={classes.action}
              >
                <Icon path={mdiOpenInNew} size={0.85} />
                Ver boleto
              </Button>
              <Button
                onClick={() => handleOpenUrl(boleto.pdf)}
                className={classes.action}
              >
                <Icon path={mdiFilePdfBox} size={0.85} />
                Baixar PDF
              </Button>
              <Button
                onClick={() => handleCopy(boleto.line)}
                className={classes.action}
              >
                <Icon path={mdiBarcodeScan} size={0.85} />
                Copiar linha
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </BaseFilterModal>
  )
}

export default AddonChargeDetails
